import React, { useState, useEffect, useRef } from "react";
import Widgets from "../../../../widgets";
import { useTranslation } from "react-i18next";
import strings from "../../../../utils/strings";
import Icons from "../../../../Assets/Icons";
import "./AdditionalConsumableList.css";
import { getLocalizedText } from "../../../../utils/localization";
import { useNavigate, useLocation } from "react-router-dom";
import queryString from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { getAddConsumableList, getAllGroups, createConsumable, createGroup } from "../../../../modules/NursignCare/AdditonalConsumable/AddConsumableThunk";
import { resetGetAddConsumableList } from "../../../../modules/NursignCare/AdditonalConsumable/AddConsumableSlice";
import ToastPopup from "../../../../widgets/Toast/toastPopup";
import * as XLSX from "xlsx";

const AdditionalConsumableList = () => {
  const dispatch = useDispatch();
  const { addConsumableListSuccess, addConsumableListErr } = useSelector((state) => state.addConsumable);
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const breadcrumbs = ["nursingCare", "manageResource"];
  const paths = [strings.navigation.additionalConsumableList, location.pathname];
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [consumableList, setConsumableList] = useState({});
  const itemsPerPage = 1;
  const [searchh, setSearch] = useState(null);
  const fileInputRef = useRef(null);

  const fetchGetConsumable = (pageNumber = currentPage, limit = itemsPerPage, searchFields = "departmentName", search = searchh) => {
    let page = pageNumber ?? currentPage ?? 1;
    dispatch(getAddConsumableList({ page, limit, search, searchFields }));
  };

  useEffect(() => {
    fetchGetConsumable();
  }, []);

  useEffect(() => {
    if (typeof searchh === "string") {
      fetchGetConsumable(1);
    }
  }, [searchh]);

  useEffect(() => {
    if (addConsumableListErr) {
      ToastPopup.error(addConsumableListErr);
    }
    if (addConsumableListSuccess && addConsumableListSuccess.status === "success") {
      setConsumableList(addConsumableListSuccess.data.consumables);
      setTotalPages(addConsumableListSuccess.data.totalPages);
      setCurrentPage(addConsumableListSuccess.data.currentPage);
    }
    dispatch(resetGetAddConsumableList());
  }, [addConsumableListSuccess, addConsumableListErr]);

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, { type: "ellipsis" }, totalPages);
      } else if (currentPage >= totalPages - 1) {
        pages.push(1, { type: "ellipsis" }, totalPages - 1, totalPages);
      } else {
        pages.push(1, { type: "ellipsis" }, currentPage, { type: "ellipsis" }, totalPages);
      }
    }
    return pages;
  };

  const handleClick = (pageNumber) => {
    fetchGetConsumable(pageNumber);
  };

  const handlePrevNext = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      fetchGetConsumable(currentPage - 1);
    } else if (direction === "next" && currentPage < totalPages) {
      fetchGetConsumable(currentPage + 1);
    }
  };

  const handleVerifyClick = (item) => {
    const flattened = {
      ...item,
      ...item.price,
      itemName: JSON.stringify(item.itemName),
      groupName: JSON.stringify(item.group.group),
      groupId: item.group._id,
    };
    delete flattened.price;
    delete flattened.group;
    const queryParams = queryString.stringify(flattened, { encode: true });
    nav(`${strings.navigation.editConsumable}?${queryParams}`);
  };

  const truncateText = (text) => {
    const displayText = getLocalizedText(text, i18n.language);
    if (displayText.length > 15) {
      return displayText.slice(0, 15) + "...";
    }
    return displayText;
  };



  const handleSearch = (query) => {
    setSearch(query);
  };

  const handleBulkUploadClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws);
      processBulkConsumables(data);
    };
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  const processBulkConsumables = async (data) => {
    let successCount = 0;
    let failCount = 0;
    let duplicateCount = 0;
    let createdGroupCount = 0;
    ToastPopup.info("Processing bulk upload... please wait.");

    let existingCodes = new Set();
    try {
      const existingResp = await dispatch(
        getAddConsumableList({
          page: 1,
          limit: 1000,
          search: "",
          searchFields: "itemName,code",
        }),
      ).unwrap();
      const existing = existingResp?.data?.consumables || [];
      existing.forEach((c) => {
        if (c.code) existingCodes.add(String(c.code).toUpperCase());
      });
    } catch (e) { }

    let groupMap = new Map();
    try {
      const grpResp = await dispatch(getAllGroups()).unwrap();
      const groups = grpResp?.data?.groups || [];
      groups.forEach((g) => {
        groupMap.set(String(g.group).toLowerCase(), g._id);
      });
    } catch (e) { }

    const toStr = (val, def = "") => {
      if (val === null || val === undefined) return def;
      const s = String(val).trim();
      return s.length > 0 ? s : def;
    };
    const normalizeUOM = (val) => {
      const s = toStr(val).toUpperCase();
      if (["PCS", "PAIR", "PACK"].includes(s)) return s;
      return s || "PCS";
    };
    const numberize = (val) => {
      const s = toStr(val);
      if (!s) return "";
      const clean = s.replace(/,/g, "");
      return /^\d+(\.\d+)?$/.test(clean) ? clean : "";
    };

    for (const row of data) {
      const rawGroupName = toStr(row["Group"] ?? row["Group Name"] ?? row.group ?? row.groupName);
      const groupKey = rawGroupName.toLowerCase();
      let groupId = groupMap.get(groupKey);

      const code = toStr(row["Code"] ?? row.code).toUpperCase();
      const itemName = toStr(row["Item Name"] ?? row.itemName);
      const stockUOM = normalizeUOM(row["Stock UOM"] ?? row.stockUOM);
      const numberOfPcs = numberize(row["Number Of Pcs"] ?? row.numberOfPcs ?? row.numPcs);
      const opdInternationalPrice = numberize(row["OPD International Price"] ?? row.opdInternationalPrice ?? row.opdInternational);
      const opdLocalPrice = numberize(row["OPD Local Price"] ?? row.opdLocalPrice ?? row.opdLocal);
      const opdSubsidized = numberize(row["OPD Subsidized Price"] ?? row.opdSubsidizedPrice ?? row.opdSubsidized);

      if (!groupId) {
        try {
          const cg = await dispatch(createGroup({ body: { group: rawGroupName } })).unwrap();
          const created =
            cg?.data?.group?._id ||
            cg?.data?._id ||
            cg?.group?._id ||
            cg?._id ||
            null;
          if (created) {
            groupId = created;
            groupMap.set(groupKey, groupId);
            createdGroupCount++;
            console.info(`Created missing group "${rawGroupName}"`);
          } else {
            try {
              const refresh = await dispatch(getAllGroups()).unwrap();
              const groups = refresh?.data?.groups || [];
              const found = groups.find((g) => String(g.group).toLowerCase() === groupKey);
              if (found?._id) {
                groupId = found._id;
                groupMap.set(groupKey, groupId);
                createdGroupCount++;
                console.info(`Created and resolved group "${rawGroupName}"`);
              }
            } catch (e) { }
          }
        } catch (err) {
          const msg = typeof err === "string" ? err : err?.message || "";
          console.error(`Row failed - Group not found and could not be created: "${rawGroupName}"`, msg);
        }
        if (!groupId) {
          failCount++;
          continue;
        }
      }
      if (!code || !itemName || !stockUOM || !numberOfPcs) {
        console.error("Row failed - Missing required fields:", { code, itemName, stockUOM, numberOfPcs });
        failCount++;
        continue;
      }
      if (existingCodes.has(code)) {
        duplicateCount++;
        continue;
      }

      const body = {
        group: groupId,
        code,
        itemName,
        stockUOM,
        numberOfPcs,
        price: {
          opdInternationalPrice,
          opdLocalPrice,
          opdSubsidized,
        },
        country: "6641f4beeb9fcc2db568c1f8",
      };

      try {
        const result = await dispatch(createConsumable({ body })).unwrap();
        if (result?.status === "success") {
          successCount++;
          existingCodes.add(code);
        } else {
          console.error("Row failed - API error:", result);
          failCount++;
        }
      } catch (error) {
        const msg = typeof error === "string" ? error : error?.message || "";
        if (msg && /exist/i.test(msg)) {
          duplicateCount++;
        } else {
          console.error("Row failed - Exception:", error);
          failCount++;
        }
      }
    }

    const total = data.length;
    if (successCount > 0) {
      ToastPopup.success(`Added ${successCount} of ${total}.`);
    }
    if (duplicateCount > 0) {
      ToastPopup.info(`Skipped ${duplicateCount} duplicates. Executed the rest.`);
    }
    if (failCount > 0) {
      ToastPopup.error(`Failed ${failCount} rows. Check console for details.`);
    }
    if (createdGroupCount > 0) {
      ToastPopup.info(`Created ${createdGroupCount} missing group(s) from the sheet.`);
    }
    if (successCount > 0) {


      const oldTotal = totalPages * itemsPerPage;
      const newTotal = oldTotal + successCount;
      const newLastPage = Math.ceil(newTotal / itemsPerPage);

      dispatch(getAddConsumableList({ page: newLastPage, limit: itemsPerPage, search: "", searchFields: "itemName,code" }));
      setCurrentPage(newLastPage);
    } else if (successCount + duplicateCount + failCount > 0) {
      // If no success but we processed something, just refresh current page
      dispatch(getAddConsumableList({ page: currentPage, limit: itemsPerPage, search: "", searchFields: "itemName,code" }));
    }
  };

  return (
    <div className="dp-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="department-heading">{t("additionalConsumables")}</div>
      <div className="department-search-line">
        <Widgets.SearchBox onSearch={handleSearch} placeholder={"searchByCodeName"} />
        <input type="file" accept=".xlsx, .xls" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />
        <div className="add-consumable-group">
          <Widgets.IconButton iconSrc={Icons.publish} buttonText={t("Bulk Upload")} onClick={handleBulkUploadClick} />
          <Widgets.IconButton iconSrc={Icons.plusIconGreen} buttonText="createConsumable" onClick={() => nav(strings.navigation.addConsumable)} />
        </div>
      </div>
      <div className="dp-list-box">
        <div className="dp-secondary-box">
          <div className="dp-price-box">
            <table className="dp-price-layout">
              <thead className="dp-thead">
                <tr>
                  <th>{t("name")}</th>
                  <th>{t("code")}</th>
                  <th>{t("stockUOM")}</th>
                  <th>{t("groupName")}</th>
                  <th>{t("opdIntInsured")}</th>
                  <th>{t("opdLocalInsured")}</th>
                  <th>{t("opdSubsidized")}</th>
                </tr>
              </thead>
              <tbody className="dp-tbody">
                {consumableList.length > 0 &&
                  consumableList.map((item, index) => (
                    <tr key={index} className="acl-table-row" onClick={() => handleVerifyClick(item)}>
                      <td className="acl-name-list" onMouseEnter={() => setHoveredIndex(index)} onMouseLeave={() => setHoveredIndex(null)}>
                        {truncateText(item.itemName)}
                        {hoveredIndex === index && <div className="acl-name-list-hover">{getLocalizedText(item.itemName, i18n.language)}</div>}
                      </td>
                      <td>{item.code}</td>
                      <td>{item.stockUOM}</td>
                      <td>{getLocalizedText(item?.group?.group, i18n.language)}</td>
                      <td>{item.price?.opdInternationalPrice}</td>
                      <td>{item.price?.opdLocalPrice}</td>
                      <td>{item.price?.opdSubsidized}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {totalPages === 0 && (
              <div className="no-record-container">
                <div className="no-record-icon">
                  <img src={Icons.NoData} alt="No Data Found" />
                </div>
                <div className="no-record-text">{t("noRecordFound")}</div>
              </div>
            )}
          </div>
          <div className="department-pagination-list">
            {totalPages > 0 && (
              <>
                <button onClick={() => handlePrevNext("prev")} disabled={currentPage === 1} className="department-pagination-prev-list">
                  <img src={Icons.leftArrow} alt="<" />
                </button>
                {renderPagination().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === "number" && handleClick(page)}
                    disabled={page === currentPage || page.type === "ellipsis"}
                    className={`department-pagination-btn-list ${page === currentPage ? "active-list" : ""} ${page.type === "ellipsis" ? "ellipse" : ""}`}
                  >
                    {typeof page === "number" ? page : <img src={Icons.extraIcon} alt="..." className="department-extra-icon" />}
                  </button>
                ))}
                <button onClick={() => handlePrevNext("next")} disabled={currentPage === totalPages} className="department-pagination-next-list">
                  <img src={Icons.rightArrow} alt=">" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalConsumableList;
